import styled from '@emotion/styled';
import { Model, Pie1D, Pie1DData } from '@gameonly/core';
import { useTranslation } from 'react-i18next';
import {
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
} from 'recharts';
import keyify from '../project/parameters/keyify';
import { getCategoryTranslationPrefix } from './Bar1DComponent';
import PdfLegend, { PdfLegendRow } from './PdfLegend';
import {
  SeedsTooltipComponent,
  SeedsTooltipRow,
} from './SeedsTooltipComponent';

type NameType = string | number;
type ValueType = string | number;

interface CustomTooltip extends TooltipProps<ValueType, NameType> {
  unit: string | undefined;
  translationPrefix: string;
  noTranslationForTooltip?: boolean;
}

const CustomTooltipComponent = ({
  active,
  payload,
  unit,
  translationPrefix,
  noTranslationForTooltip = false,
}: CustomTooltip): JSX.Element => {
  const { t } = useTranslation(['project', 'model']);
  if (active && payload && payload.length > 0) {
    const category = payload[0].payload;
    const categoryName = noTranslationForTooltip
      ? category.name // usefull for cnc report pie on report page
      : category.name.startsWith('#')
      ? category.name.slice(1) // usefull for annual reports results
      : t(`${translationPrefix}.data.${keyify(category.name)}`);
    return (
      <SeedsTooltipComponent>
        <SeedsTooltipRow
          color={category.fill}
          category={categoryName}
          value={category.value}
          unit={unit}
        />
      </SeedsTooltipComponent>
    );
  }
  return <></>;
};

export const Pie1DComponent = ({
  result,
  model,
  noTranslationForTooltip = false,
  compact,
}: {
  result: Pie1D;
  model?: Model;
  noTranslationForTooltip?: boolean;
  compact?: boolean;
}) => {
  const { t } = useTranslation(['project', 'model']);
  const translationPrefix = `model:${model?.name.toLowerCase()}.result.${result.code?.toLowerCase()}`;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={result.data}
          cx={'50%'}
          cy={'50%'}
          innerRadius="0%"
          outerRadius="100%"
          dataKey="value"
        />
        <Tooltip
          content={
            <CustomTooltipComponent
              unit={model ? t(`${translationPrefix}.unit`) : result.unit}
              translationPrefix={translationPrefix}
              noTranslationForTooltip={noTranslationForTooltip}
            />
          }
          allowEscapeViewBox={{ x: true, y: true }}
          wrapperStyle={{ zIndex: 1000 }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

const LegendContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  > div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}
`;

export const Pie1DLegend = ({
  result,
  model,
  forPDF,
  compact,
  color,
}: {
  result: Pie1D;
  model?: Model;
  forPDF?: boolean;
  compact?: boolean;
  color?: string;
}) => {
  const { t } = useTranslation(['project', 'model']);
  const data = JSON.parse(
    JSON.stringify(result.data?.sort((a, b) => b.value - a.value)),
  );
  const total = data.reduce(
    (total: number, row: Pie1DData) => total + row.value,
    0,
  );
  const translationPrefix = getCategoryTranslationPrefix(model, result);

  if (forPDF) {
    return (
      <PdfLegend compact={compact} color={color}>
        {data.map((row: Pie1DData, index: number) => (
          <>
            {row.value > 0 && (
              <PdfLegendRow
                key={row.name}
                link={row.link}
                color={row.fill}
                category={
                  row.name.startsWith('#')
                    ? row.name.slice(1)
                    : t(`${translationPrefix}.data.${keyify(row.name)}`)
                }
                value={row.value.toString()}
                unit={t(`${translationPrefix}.unit`)}
                percent={row.value / total}
                compact={compact}
              />
            )}
          </>
        ))}
      </PdfLegend>
    );
  }

  return (
    <LegendContainer>
      <p className="h6r underline">{t('project:results-page.legend')}</p>
      <div>
        {data.map((row: Pie1DData) => (
          <>
            {row.value > 0 && (
              <SeedsTooltipRow
                key={row.name}
                link={row.link}
                color={row.fill}
                category={
                  row.name.startsWith('#')
                    ? row.name.slice(1)
                    : t(`${translationPrefix}.data.${keyify(row.name)}`)
                }
                value={row.value.toString()}
                unit={t(`${translationPrefix}.unit`)}
                percent={row.value / total}
              />
            )}
          </>
        ))}
      </div>
    </LegendContainer>
  );
};

export default Pie1DComponent;
